/**
 * Assistant memory CRUD (request 0317) — `/ai/v1/memory`.
 * Server owns prompt injection (0316); this client is for the tool + audit screen only.
 */

import { systemApiFetch } from '@/services/systemApiClient'

const AI_MOUNT = '/ai/v1'

/** Same cap as `AI_MEMORY_TEXT_MAX` on the server — validate before the round-trip. */
export const AI_MEMORY_TEXT_MAX = 200

export type AiMemoryScope = 'org' | 'user'
export type AiMemoryProduct = 'syscolect' | 'vectorhub'
export type AiMemorySource = 'assistant' | 'user'

export type AiMemoryRecord = {
  id: string
  organizationId: string
  scope: AiMemoryScope
  userId: string | null
  product: AiMemoryProduct
  text: string
  source: AiMemorySource
  active: boolean
  inclusionDate: string | null
  lastModifiedDate: string | null
  inclusionUserId: string | null
  lastModifiedUserId: string | null
}

export type CreateAiMemoryInput = {
  scope: AiMemoryScope
  product: AiMemoryProduct
  text: string
  source: AiMemorySource
}

export type UpdateAiMemoryInput = {
  text?: string
  active?: boolean
}

export function fetchAiMemories(
  orgId: string,
  product: AiMemoryProduct = 'syscolect',
): Promise<AiMemoryRecord[]> {
  const q = new URLSearchParams({ product })
  return systemApiFetch<{ memories: AiMemoryRecord[] }>(
    AI_MOUNT,
    `/memory?${q}`,
    { method: 'GET', orgId },
  ).then((body) => body.memories ?? [])
}

export function createAiMemory(
  orgId: string,
  input: CreateAiMemoryInput,
): Promise<AiMemoryRecord> {
  return systemApiFetch<AiMemoryRecord>(AI_MOUNT, '/memory', {
    method: 'POST',
    orgId,
    body: JSON.stringify(input),
  })
}

export function updateAiMemory(
  orgId: string,
  id: string,
  patch: UpdateAiMemoryInput,
): Promise<AiMemoryRecord> {
  return systemApiFetch<AiMemoryRecord>(
    AI_MOUNT,
    `/memory/${encodeURIComponent(id)}`,
    {
      method: 'PATCH',
      orgId,
      body: JSON.stringify(patch),
    },
  )
}

export function deleteAiMemory(orgId: string, id: string): Promise<void> {
  return systemApiFetch<unknown>(
    AI_MOUNT,
    `/memory/${encodeURIComponent(id)}`,
    { method: 'DELETE', orgId },
  ).then(() => undefined)
}
